import { usePDF } from "@react-pdf/renderer";
import { Check } from "lucide-react";
import React from "react";

import { sharedMessages } from "../shared.intl";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import { customToast } from "@/components/alerts/CustomToast";
import type { BtnProps } from "@/components/headers";
import { ReportTypes } from "@/features/roster/roster.constants";
import { ReportTypeKey } from "@/features/roster/roster.types";
import { Permissions } from "@/interfaces/globals";
import { hasWritePermission } from "@/redux/slices/permissionSlice";
import { useAppSelector } from "@/redux/store";

interface UseForceSubmitReportOptions {
  onForceSubmit?: (pdfFile: File | null) => Promise<void>;
  generatePdf?: boolean;
  fileName?: string;
  onSuccess?: () => void;
  pdfDocument?: React.ReactElement;
  reportType?: ReportTypeKey;
}

interface UseForceSubmitReportReturn {
  isForceSubmitting: boolean;
  handleForceSubmit: () => Promise<void>;
  buildForceSubmitBtnProps: (args: {
    show: boolean;
    disabled?: boolean;
  }) => BtnProps;
}

const PDF_WAIT_TIMEOUT_MS = 30_000;
const PDF_POLL_INTERVAL_MS = 100;

/**
 * Builds force-submit button behavior with optional PDF generation.
 *
 * @param {UseForceSubmitReportOptions} options - Submission and PDF options.
 * @returns {UseForceSubmitReportReturn} Submission state and button helpers.
 */
export const useForceSubmitReport = (
  options: UseForceSubmitReportOptions,
): UseForceSubmitReportReturn => {
  const t = useIntlMessages(sharedMessages);
  const {
    onForceSubmit,
    generatePdf = false,
    fileName = "force-submitted-report.pdf",
    onSuccess,
    pdfDocument,
    reportType,
  } = options;

  const [isForceSubmitting, setIsForceSubmitting] = React.useState(false);

  const permission = React.useMemo(() => {
    switch (reportType) {
      case ReportTypes.DAILY_REPORT:
        return Permissions.DAILY_REPORT;
      case ReportTypes.DOCKET_REPORT:
        return Permissions.REPORTS_WORKDOCKET;
      case ReportTypes.TIPPING_RECORDS:
        return Permissions.REPORTS_TIPPING;
      default:
        return Permissions.REPORTS;
    }
  }, [reportType]);

  const canWriteReport = useAppSelector(hasWritePermission(permission));

  const [pdfInstance, updatePdfInstance] = usePDF({});

  const pdfInstanceRef = React.useRef(pdfInstance);
  React.useEffect(() => {
    pdfInstanceRef.current = pdfInstance;
  }, [pdfInstance]);

  React.useEffect(() => {
    if (pdfDocument) {
      updatePdfInstance(pdfDocument);
    }
  }, [pdfDocument, updatePdfInstance]);

  const waitForPdfBlob = React.useCallback((): Promise<Blob | null> => {
    if (!pdfDocument) {
      return Promise.resolve(null);
    }

    const latest = pdfInstanceRef.current;
    if (latest.blob) {
      return Promise.resolve(latest.blob);
    }
    if (latest.error) {
      return Promise.reject(new Error(latest.error));
    }

    return new Promise((resolve, reject) => {
      const started = Date.now();
      const intervalId = window.setInterval(() => {
        const { blob, error } = pdfInstanceRef.current;

        if (blob) {
          window.clearInterval(intervalId);
          resolve(blob);
        } else if (error) {
          window.clearInterval(intervalId);
          reject(new Error(error));
        } else if (Date.now() - started > PDF_WAIT_TIMEOUT_MS) {
          window.clearInterval(intervalId);
          reject(new Error("PDF generation timed out"));
        }
      }, PDF_POLL_INTERVAL_MS);
    });
  }, [pdfDocument]);

  const buildPdfFile = React.useCallback(async (): Promise<File | null> => {
    if (!generatePdf) {
      return null;
    }

    if (pdfDocument) {
      const blob = await waitForPdfBlob();

      return blob
        ? new File([blob], fileName, { type: "application/pdf" })
        : null;
    }

    return null;
  }, [generatePdf, pdfDocument, fileName, waitForPdfBlob]);

  const handleForceSubmit = React.useCallback(async (): Promise<void> => {
    if (!onForceSubmit || isForceSubmitting) {
      return;
    }

    setIsForceSubmitting(true);
    try {
      const pdfFile = await buildPdfFile();
      await onForceSubmit(pdfFile);
      onSuccess?.();
    } catch {
      if (pdfInstanceRef.current.error) {
        customToast.error(t("pdfGenerationError"));
      }
    } finally {
      setIsForceSubmitting(false);
    }
  }, [onForceSubmit, isForceSubmitting, buildPdfFile, onSuccess, t]);

  const buildForceSubmitBtnProps = React.useCallback(
    ({
      show,
      disabled = false,
    }: {
      show: boolean;
      disabled?: boolean;
    }): BtnProps => ({
      showBtn: show && canWriteReport,
      btnName: isForceSubmitting
        ? t("forceSubmitBtnSubmitting")
        : t("forceSubmitBtn"),
      handleBtnClick: handleForceSubmit,
      isLoading: isForceSubmitting,
      disabled: isForceSubmitting || disabled,
      variant: "default",
      icon: <Check className='mr-2 h-4 w-4' />,
    }),
    [canWriteReport, handleForceSubmit, isForceSubmitting, t],
  );

  return {
    isForceSubmitting,
    handleForceSubmit,
    buildForceSubmitBtnProps,
  };
};
